/**
 * Visual effect presets for artwork
 * Each effect defines CSS filter + overlay properties for preview,
 * and can be applied via CSS on the artwork image.
 */

/** Effect category for grouping in the UI */
export type EffectCategory = 'color' | 'lighting' | 'texture' | 'artistic';

/** Parameter definition for an effect */
export interface EffectParam {
  key: string;
  label: string;
  min: number;
  max: number;
  step: number;
  defaultValue: number;
}

/** CSS-based effect result */
export interface EffectCSS {
  /** CSS filter string to apply to the image */
  filter?: string;
  /** CSS background-image for overlay (gradient, pattern) */
  overlay?: string;
  /** CSS mix-blend-mode for the overlay */
  overlayBlendMode?: string;
  /** Overlay opacity (0-1) */
  overlayOpacity?: number;
}

/** Effect preset definition */
export interface EffectPresetDef {
  id: string;
  name: string;
  category: EffectCategory;
  /** Parameters the user can adjust */
  params: EffectParam[];
  /** Generate CSS properties from parameter values */
  applyCSS: (params: Record<string, number>) => EffectCSS;
}

// === Color Presets ===

const sepiaPreset: EffectPresetDef = {
  id: 'sepia',
  name: 'Sepia',
  category: 'color',
  params: [{ key: 'intensity', label: 'Intensity', min: 0, max: 100, step: 5, defaultValue: 60 }],
  applyCSS: (p) => ({
    filter: `sepia(${(p.intensity ?? 60) / 100}) saturate(${1.2 - (p.intensity ?? 60) / 200})`,
  }),
};

const vintagePreset: EffectPresetDef = {
  id: 'vintage',
  name: 'Vintage',
  category: 'color',
  params: [{ key: 'intensity', label: 'Intensity', min: 0, max: 100, step: 5, defaultValue: 50 }],
  applyCSS: (p) => {
    const i = (p.intensity ?? 50) / 100;
    return {
      filter: `sepia(${i * 0.4}) saturate(${1 - i * 0.3}) contrast(${1 + i * 0.1}) brightness(${1 + i * 0.05})`,
      overlay: `radial-gradient(ellipse at center, transparent 50%, rgba(120,80,40,${i * 0.25}) 100%)`,
      overlayBlendMode: 'multiply',
      overlayOpacity: i,
    };
  },
};

const bwPreset: EffectPresetDef = {
  id: 'bw',
  name: 'B&W',
  category: 'color',
  params: [
    { key: 'intensity', label: 'Intensity', min: 0, max: 100, step: 5, defaultValue: 100 },
    { key: 'contrast', label: 'Contrast', min: -50, max: 50, step: 5, defaultValue: 10 },
  ],
  applyCSS: (p) => ({
    filter: `grayscale(${(p.intensity ?? 100) / 100}) contrast(${1 + (p.contrast ?? 10) / 100})`,
  }),
};

const warmPreset: EffectPresetDef = {
  id: 'warm',
  name: 'Warm',
  category: 'color',
  params: [{ key: 'intensity', label: 'Warmth', min: 0, max: 100, step: 5, defaultValue: 40 }],
  applyCSS: (p) => {
    const i = (p.intensity ?? 40) / 100;
    return {
      filter: `sepia(${i * 0.25}) saturate(${1 + i * 0.2}) brightness(${1 + i * 0.05})`,
    };
  },
};

const coolPreset: EffectPresetDef = {
  id: 'cool',
  name: 'Cool',
  category: 'color',
  params: [{ key: 'intensity', label: 'Coolness', min: 0, max: 100, step: 5, defaultValue: 40 }],
  applyCSS: (p) => {
    const i = (p.intensity ?? 40) / 100;
    return {
      filter: `hue-rotate(${-i * 15}deg) saturate(${1 - i * 0.15}) brightness(${1 + i * 0.03})`,
    };
  },
};

const fadedPreset: EffectPresetDef = {
  id: 'faded',
  name: 'Faded',
  category: 'color',
  params: [{ key: 'intensity', label: 'Intensity', min: 0, max: 100, step: 5, defaultValue: 50 }],
  applyCSS: (p) => {
    const i = (p.intensity ?? 50) / 100;
    return {
      filter: `saturate(${1 - i * 0.5}) contrast(${1 - i * 0.15}) brightness(${1 + i * 0.1})`,
    };
  },
};

// === Lighting Effects ===

const vignettePreset: EffectPresetDef = {
  id: 'vignette',
  name: 'Vignette',
  category: 'lighting',
  params: [
    { key: 'intensity', label: 'Intensity', min: 0, max: 100, step: 5, defaultValue: 50 },
    { key: 'size', label: 'Size', min: 20, max: 80, step: 5, defaultValue: 40 },
  ],
  applyCSS: (p) => ({
    overlay: `radial-gradient(ellipse at center, transparent ${p.size ?? 40}%, rgba(0,0,0,${((p.intensity ?? 50) / 100) * 0.7}) 100%)`,
    overlayBlendMode: 'multiply',
    overlayOpacity: 1,
  }),
};

const sunlightPreset: EffectPresetDef = {
  id: 'sunlight',
  name: 'Sunlight',
  category: 'lighting',
  params: [
    { key: 'intensity', label: 'Intensity', min: 0, max: 100, step: 5, defaultValue: 40 },
    { key: 'angle', label: 'Angle', min: 0, max: 360, step: 15, defaultValue: 135 },
  ],
  applyCSS: (p) => {
    const i = (p.intensity ?? 40) / 100;
    const angle = p.angle ?? 135;
    return {
      filter: `brightness(${1 + i * 0.15}) saturate(${1 + i * 0.1})`,
      overlay: `linear-gradient(${angle}deg, rgba(255,220,150,${i * 0.35}) 0%, transparent 60%)`,
      overlayBlendMode: 'screen',
      overlayOpacity: 1,
    };
  },
};

const windowShadePreset: EffectPresetDef = {
  id: 'window-shade',
  name: 'Window Shade',
  category: 'lighting',
  params: [
    { key: 'intensity', label: 'Intensity', min: 0, max: 100, step: 5, defaultValue: 40 },
    { key: 'spacing', label: 'Spacing', min: 5, max: 30, step: 1, defaultValue: 12 },
  ],
  applyCSS: (p) => {
    const i = (p.intensity ?? 40) / 100;
    const spacing = p.spacing ?? 12;
    return {
      overlay: `repeating-linear-gradient(0deg, transparent 0px, transparent ${spacing - 2}px, rgba(0,0,0,${i * 0.3}) ${spacing - 2}px, rgba(0,0,0,${i * 0.3}) ${spacing}px)`,
      overlayBlendMode: 'multiply',
      overlayOpacity: 1,
    };
  },
};

const goldenHourPreset: EffectPresetDef = {
  id: 'golden-hour',
  name: 'Golden Hour',
  category: 'lighting',
  params: [{ key: 'intensity', label: 'Intensity', min: 0, max: 100, step: 5, defaultValue: 45 }],
  applyCSS: (p) => {
    const i = (p.intensity ?? 45) / 100;
    return {
      filter: `sepia(${i * 0.3}) saturate(${1 + i * 0.4}) brightness(${1 + i * 0.1})`,
      overlay: `linear-gradient(180deg, rgba(255,180,80,${i * 0.2}) 0%, rgba(255,120,50,${i * 0.15}) 100%)`,
      overlayBlendMode: 'soft-light',
      overlayOpacity: 1,
    };
  },
};

const spotlightPreset: EffectPresetDef = {
  id: 'spotlight',
  name: 'Spotlight',
  category: 'lighting',
  params: [
    { key: 'intensity', label: 'Intensity', min: 0, max: 100, step: 5, defaultValue: 50 },
    { key: 'size', label: 'Spot Size', min: 20, max: 70, step: 5, defaultValue: 35 },
  ],
  applyCSS: (p) => {
    const i = (p.intensity ?? 50) / 100;
    const size = p.size ?? 35;
    return {
      overlay: `radial-gradient(ellipse at center, rgba(255,255,255,${i * 0.2}) 0%, transparent ${size}%, rgba(0,0,0,${i * 0.5}) 100%)`,
      overlayBlendMode: 'soft-light',
      overlayOpacity: 1,
    };
  },
};

// === Texture Effects ===

const filmGrainPreset: EffectPresetDef = {
  id: 'film-grain',
  name: 'Film Grain',
  category: 'texture',
  params: [{ key: 'intensity', label: 'Grain', min: 0, max: 100, step: 5, defaultValue: 30 }],
  applyCSS: (p) => {
    const i = (p.intensity ?? 30) / 100;
    // SVG-based noise pattern for grain
    const noiseSvg = `<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='g'><feTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/><feColorMatrix type='saturate' values='0'/></filter><rect width='100%' height='100%' filter='url(%23g)' opacity='${i * 0.4}'/></svg>`;
    const encoded = btoa(noiseSvg);
    return {
      overlay: `url("data:image/svg+xml;base64,${encoded}")`,
      overlayBlendMode: 'overlay',
      overlayOpacity: i,
    };
  },
};

const dustPreset: EffectPresetDef = {
  id: 'dust',
  name: 'Dust & Scratches',
  category: 'texture',
  params: [{ key: 'intensity', label: 'Intensity', min: 0, max: 100, step: 5, defaultValue: 30 }],
  applyCSS: (p) => {
    const i = (p.intensity ?? 30) / 100;
    const noiseSvg = `<svg xmlns='http://www.w3.org/2000/svg' width='300' height='300'><filter id='d'><feTurbulence type='turbulence' baseFrequency='0.02' numOctaves='3' seed='5' stitchTiles='stitch'/><feColorMatrix type='saturate' values='0'/></filter><rect width='100%' height='100%' filter='url(%23d)' opacity='${i * 0.3}'/></svg>`;
    const encoded = btoa(noiseSvg);
    return {
      overlay: `url("data:image/svg+xml;base64,${encoded}")`,
      overlayBlendMode: 'screen',
      overlayOpacity: i * 0.6,
    };
  },
};

// === Artistic Effects ===

const dramaticPreset: EffectPresetDef = {
  id: 'dramatic',
  name: 'Dramatic',
  category: 'artistic',
  params: [{ key: 'intensity', label: 'Intensity', min: 0, max: 100, step: 5, defaultValue: 50 }],
  applyCSS: (p) => {
    const i = (p.intensity ?? 50) / 100;
    return {
      filter: `contrast(${1 + i * 0.4}) saturate(${1 + i * 0.2}) brightness(${1 - i * 0.05})`,
      overlay: `radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,${i * 0.3}) 100%)`,
      overlayBlendMode: 'multiply',
      overlayOpacity: 1,
    };
  },
};

const softGlowPreset: EffectPresetDef = {
  id: 'soft-glow',
  name: 'Soft Glow',
  category: 'artistic',
  params: [{ key: 'intensity', label: 'Glow', min: 0, max: 100, step: 5, defaultValue: 35 }],
  applyCSS: (p) => {
    const i = (p.intensity ?? 35) / 100;
    return {
      filter: `brightness(${1 + i * 0.15}) contrast(${1 - i * 0.1}) saturate(${1 - i * 0.1})`,
      overlay: `radial-gradient(ellipse at center, rgba(255,255,255,${i * 0.15}) 0%, transparent 70%)`,
      overlayBlendMode: 'screen',
      overlayOpacity: 1,
    };
  },
};

const moodyPreset: EffectPresetDef = {
  id: 'moody',
  name: 'Moody',
  category: 'artistic',
  params: [{ key: 'intensity', label: 'Intensity', min: 0, max: 100, step: 5, defaultValue: 45 }],
  applyCSS: (p) => {
    const i = (p.intensity ?? 45) / 100;
    return {
      filter: `saturate(${1 - i * 0.3}) contrast(${1 + i * 0.2}) brightness(${1 - i * 0.1})`,
      overlay: `linear-gradient(180deg, rgba(30,30,60,${i * 0.2}) 0%, rgba(40,20,20,${i * 0.15}) 100%)`,
      overlayBlendMode: 'soft-light',
      overlayOpacity: 1,
    };
  },
};

const lightLeakPreset: EffectPresetDef = {
  id: 'light-leak',
  name: 'Light Leak',
  category: 'artistic',
  params: [{ key: 'intensity', label: 'Intensity', min: 0, max: 100, step: 5, defaultValue: 40 }],
  applyCSS: (p) => {
    const i = (p.intensity ?? 40) / 100;
    return {
      overlay: `linear-gradient(135deg, rgba(255,100,50,${i * 0.3}) 0%, transparent 50%, rgba(255,200,100,${i * 0.2}) 100%)`,
      overlayBlendMode: 'screen',
      overlayOpacity: 1,
    };
  },
};

// === All effect presets ===

export const EFFECT_PRESETS: EffectPresetDef[] = [
  // Color
  sepiaPreset,
  vintagePreset,
  bwPreset,
  warmPreset,
  coolPreset,
  fadedPreset,
  // Lighting
  vignettePreset,
  sunlightPreset,
  windowShadePreset,
  goldenHourPreset,
  spotlightPreset,
  // Texture
  filmGrainPreset,
  dustPreset,
  // Artistic
  dramaticPreset,
  softGlowPreset,
  moodyPreset,
  lightLeakPreset,
];

/** Effect categories for the UI */
export const EFFECT_CATEGORIES: { id: EffectCategory | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'color', label: 'Color' },
  { id: 'lighting', label: 'Light' },
  { id: 'texture', label: 'Texture' },
  { id: 'artistic', label: 'Artistic' },
];

/** Look up an effect preset by template ID */
export function getEffectPreset(templateId: string): EffectPresetDef | undefined {
  return EFFECT_PRESETS.find((e) => e.id === templateId);
}

/** Get default parameter values for an effect */
export function getEffectDefaults(preset: EffectPresetDef): Record<string, number> {
  const defaults: Record<string, number> = {};
  for (const param of preset.params) {
    defaults[param.key] = param.defaultValue;
  }
  return defaults;
}

/** Compute CSS properties for an effect with given parameters */
export function computeEffectCSS(
  templateId: string,
  params: Record<string, number | number[] | boolean> | undefined,
): EffectCSS | null {
  const preset = getEffectPreset(templateId);
  if (!preset) return null;

  // Convert params to number-only record, using defaults for missing keys
  const numericParams: Record<string, number> = {};
  for (const p of preset.params) {
    const val = params?.[p.key];
    numericParams[p.key] = typeof val === 'number' ? val : p.defaultValue;
  }

  return preset.applyCSS(numericParams);
}
